import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import CustomCheckbox from "../../table/CustomCheckbox";
import HeaderActionItem from "../../../../../assets/common/table/HeaderActionItem";
import ActionItem from "../../../../../assets/common/table/ActionItem";
import AvatarCell from "../../common/AvatarCell";
import { getAsyncSelectStyles } from "../../../../../utils/asynSelectStyles";
import useRTLContext from "../../../../../hooks/useRTLContext";
import styles from "../../../../../style/cssModules/table/TableComponent.module.css";
import { useCreatedByOptions } from "./useCreatedByOptions";
import { useStatusOptions } from "./useStatusOptions";
import { useSelectSync } from "../../../../../hooks/useStateSync";
import { useEnsureOption } from "../../../../../hooks/useEnsureOption";
import { getEntityOptionsByFilter } from "../../../../../utils/getOptions";
import MainServices from "../../../../../services/MainServices";

export function useReligionColumns({
  data,
  allSelected,
  toggleSelectAll,
  toggleRowSelection,
  handleEdit,
  handleDeactivate,
  handleDelete,
  handleBulkStatusUpdate,
  filters,
  addFilter,
  removeFilter,
}) {
  const { getEmployeesPersonalInfoMaster } = MainServices();
  const { t } = useTranslation();
  const { isRTL } = useRTLContext();
  const { get } = MainServices();

  const {
    createdByOptions,
    selectedCreatedBy,
    setSelectedCreatedBy,
    onSearchCreatedByDebounce,
    setCreatedByOptions,
  } = useCreatedByOptions({
    addFilter,
    removeFilter,
    filters,
  });

  const {
    statusOptions,
    selectedStatus,
    setSelectedStatus,
    onSearchStatusDebounce,
  } = useStatusOptions({
    addFilter,
    filters,
  });

  const getCreatedByOptionsById = (publicId) =>
    getEntityOptionsByFilter(
      getEmployeesPersonalInfoMaster,
      "publicId",
      publicId,
      { labelField: "email", valueField: "publicId" },
      "data.data.doc"
    );

  useEnsureOption({
    filterKey: "createdBy",
    addedFilters: filters,
    options: createdByOptions,
    setOptions: setCreatedByOptions,
    setSelected: setSelectedCreatedBy, // 🔹 pass setter
    fetchByValue: getCreatedByOptionsById,
  });

  useSelectSync({
    filterKey: "createdBy",
    options: createdByOptions,
    addedFilters: filters,
    setSelected: setSelectedCreatedBy,
  });

  const defaultStatusArray = ["active", "inactive"];

  useSelectSync({
    filterKey: "status",
    options: statusOptions,
    addedFilters: filters,
    setSelected: setSelectedStatus,
    selected: selectedStatus, // ← pass it here
    getFilterValue: (f) => {
      if (!f) return null;
      if (
        Array.isArray(f.value) &&
        defaultStatusArray.every((v) => f.value.includes(v))
      )
        return null; // treat default array as "no filter"
      return f.value; // either "active" or "inactive"
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <CustomCheckbox checked={allSelected} onChange={toggleSelectAll} />
        ),
        accessor: "select",
        Cell: ({ row }) => (
          <CustomCheckbox
            checked={row.original.isSelected || false}
            onChange={() => toggleRowSelection(row.index)}
          />
        ),
        style: {
          width: "64px",
          maxWidth: "64px",
          textAlign: "center",
          padding: "10px 12px",
        },
      },
      {
        Header: t("Code"),
        accessor: "code",
      },
      {
        Header: t("Religion Name"),
        accessor: "name",
      },
      {
        Header: t("Description"),
        accessor: "description",
        Cell: ({ value }) =>
          value?.trim() ? value : t("No description available"),
      },
      {
        Header: () => (
          <AsyncSelect
            id="createdBy"
            name="createdBy"
            value={selectedCreatedBy}
            onChange={(selectedOption) => {
              setSelectedCreatedBy(selectedOption);
              if (selectedOption) {
                if (
                  !createdByOptions.some(
                    (o) => o.value === selectedOption.value
                  )
                ) {
                  setCreatedByOptions((prev) => [...prev, selectedOption]);
                }
                addFilter("createdBy", selectedOption.value, "createdBy");
              } else if (filters?.hasOwnProperty("createdBy")) {
                removeFilter("createdBy");
              }
            }}
            loadOptions={onSearchCreatedByDebounce}
            defaultOptions={createdByOptions}
            styles={getAsyncSelectStyles({
              minWidth: "200px",
              autoWidth: true,
              fitInFlex: false,
            })}
            menuPortalTarget={document.body}
            cacheOptions
            placeholder={t("Author/Creator")}
            isClearable={true}
          />
        ),
        accessor: "author",
        isFiltered: !!selectedCreatedBy,
        Cell: ({ value }) => <AvatarCell data={value} />,
      },
      {
        Header: () => (
          <AsyncSelect
            id="status"
            name="status"
            value={selectedStatus}
            onChange={(selectedOption) => {
              if (
                selectedOption?.value === selectedStatus?.value ||
                (!selectedOption && !selectedStatus)
              ) {
                return;
              }

              setSelectedStatus(selectedOption);

              if (!selectedOption || selectedOption.value === "") {
                addFilter(
                  "status",
                  ["active", "inactive"],
                  "status",
                  false,
                  "in"
                );
              } else {
                addFilter("status", selectedOption.value);
              }
            }}
            loadOptions={onSearchStatusDebounce}
            defaultOptions={statusOptions}
            styles={getAsyncSelectStyles({
              minWidth: selectedStatus ? "160px" : "120px",
              autoWidth: true,
              fitInFlex: true,
            })}
            menuPortalTarget={document.body}
            cacheOptions
            isClearable={true}
            placeholder={t("Status")}
          />
        ),
        accessor: "status",
        isFiltered: !!selectedStatus,
        Cell: ({ value }) => {
          let formattedValue = "";
          let statusClass = "";
          let dotClass = "";

          if (value === "active") {
            formattedValue = "Active";
            statusClass = styles.statusActive;
            dotClass = styles.activeDot;
          } else if (value === "inactive") {
            formattedValue = "Inactive";
            statusClass = styles.statusInactive;
            dotClass = styles.inactiveDot;
          } else if (value === "on hold") {
            statusClass = styles.statusOnHold;
          } else if (value === "Pending" || value === "In progress") {
            statusClass = styles.statusPending;
          } else if (value === "Approved") {
            statusClass = styles.statusApproved;
          } else if (value === "Rejected") {
            statusClass = styles.statusRejected;
          } else if (value === "Completed") {
            statusClass = styles.statusCompleted;
          } else if (value === "cancelled") {
            statusClass = styles.statusCancelled;
          } else if (value === "draft") {
            statusClass = styles.draft;
            dotClass = styles.draftDot;
          } else if (value === "delete") {
            statusClass = styles.statusDelete;
            dotClass = styles.deleteDot;
          }

          return (
            <div className={statusClass}>
              <span className={dotClass}></span>
              {t(formattedValue) || t(value)}
            </div>
          );
        },
        style: {
          width: !!selectedStatus ? "200px" : "150px",
        },
      },
      {
        Header: () => (
          <HeaderActionItem
            data={data}
            handleBulkStatusUpdate={handleBulkStatusUpdate}
            permissionName={"Religion"}
          />
        ),
        accessor: "action",
        Cell: ({ row }) => (
          <ActionItem
            row={row.original}
            rowIndex={row.index}
            handleEdit={handleEdit}
            handleDeactivate={handleDeactivate}
            handleDelete={handleDelete}
            permissionName={"Religion"}
            moduleName={t("Religion")}
          />
        ),
        style: {
          width: "40px",
          minWidth: "40px",
          maxWidth: "40px",
          textAlign: "center",
          padding: "10px 12px",
        },
      },
    ],
    [
      createdByOptions,
      selectedCreatedBy,
      selectedStatus,
      statusOptions,
      allSelected, // keep what the header needs
      toggleSelectAll,
      toggleRowSelection, // keep handlers
      handleBulkStatusUpdate, // keep bulk handler
      handleEdit,
      handleDeactivate,
      handleDelete,
      data,
      t,
    ]
  );

  return { columns };
}
